import { useState, useEffect } from 'react'
import {
  Card,
  CardContent,
  Typography,
  FormControlLabel,
  Switch,
  Stack,
  Box,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material'
import { Save, Notifications } from '@mui/icons-material'
import { useNotificationStore } from '@/store/useNotificationStore'
import { useAuthStore } from '@/store/useAuthStore'
import { requestNotificationPermission } from '@/lib/notifications'

const reminderTimeOptions = [
  { value: 5, label: '5 minutes before' },
  { value: 15, label: '15 minutes before' },
  { value: 30, label: '30 minutes before' },
  { value: 60, label: '1 hour before' },
  { value: 120, label: '2 hours before' },
  { value: 1440, label: '1 day before' },
]

export default function NotificationSettings() {
  const currentUserId = useAuthStore((state) => state.currentUserId)
  const { preferences, loading, fetchPreferences, updatePreferences, initializePreferences } = useNotificationStore()

  const [formData, setFormData] = useState({
    enabled: true,
    reminder_time: 60,
    daily_summary: true,
    low_stock: true,
    achievement_unlocks: true,
    browser_notifications: true,
  })

  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [browserPermission, setBrowserPermission] = useState<NotificationPermission>('default')

  useEffect(() => {
    if (currentUserId) {
      fetchPreferences(currentUserId).then(() => {
        // If no preferences exist, initialize them
        if (!preferences) {
          initializePreferences(currentUserId)
        }
      })
    }
  }, [currentUserId, fetchPreferences, initializePreferences])

  useEffect(() => {
    if (preferences) {
      setFormData({
        enabled: preferences.enabled,
        reminder_time: preferences.reminder_time,
        daily_summary: preferences.daily_summary,
        low_stock: preferences.low_stock,
        achievement_unlocks: preferences.achievement_unlocks,
        browser_notifications: preferences.browser_notifications,
      })
    }
  }, [preferences])

  useEffect(() => {
    // Check browser notification permission
    if ('Notification' in window) {
      setBrowserPermission(Notification.permission)
    }
  }, [])

  const handleChange = (field: keyof typeof formData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
    }))
    setSuccess(false)
  }

  const handleEnableBrowserNotifications = async () => {
    const permission = await requestNotificationPermission()
    setBrowserPermission(permission)
    if (permission === 'granted') {
      setFormData((prev) => ({ ...prev, browser_notifications: true }))
    }
  }

  const handleSave = async () => {
    if (!currentUserId) return

    setSaving(true)
    setSuccess(false)

    try {
      await updatePreferences(currentUserId, formData)
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (error) {
      console.error('Error saving preferences:', error)
    } finally {
      setSaving(false)
    }
  }

  if (loading && !preferences) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Notifications color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Notification Settings
          </Typography>
        </Box>

        {browserPermission !== 'granted' && (
          <Alert
            severity="warning"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleEnableBrowserNotifications}
              >
                Enable
              </Button>
            }
            sx={{ mb: 3 }}
          >
            Browser notifications are not enabled. Click "Enable" to receive notifications.
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Notification settings saved successfully!
          </Alert>
        )}

        <Stack spacing={3}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.enabled}
                onChange={handleChange('enabled')}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  Enable Notifications
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Master switch for all notifications
                </Typography>
              </Box>
            }
          />

          <Box>
            <TextField
              select
              fullWidth
              label="Task Reminder Time"
              value={formData.reminder_time}
              onChange={handleChange('reminder_time')}
              disabled={!formData.enabled}
            >
              {reminderTimeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              When to notify you before a task is due
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={formData.daily_summary}
                onChange={handleChange('daily_summary')}
                color="primary"
                disabled={!formData.enabled}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  Daily Summary
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Receive a summary of your tasks each morning
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.low_stock}
                onChange={handleChange('low_stock')}
                color="primary"
                disabled={!formData.enabled}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  Low Stock Alerts
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Get notified when supplies are running low
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.achievement_unlocks}
                onChange={handleChange('achievement_unlocks')}
                color="primary"
                disabled={!formData.enabled}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  Achievement Unlocks
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Celebrate when you unlock new achievements
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.browser_notifications}
                onChange={handleChange('browser_notifications')}
                color="primary"
                disabled={!formData.enabled || browserPermission !== 'granted'}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  Browser Notifications
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Show notifications even when app is in background
                </Typography>
              </Box>
            }
          />

          <Button
            variant="contained"
            size="large"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            sx={{
              mt: 2,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  )
}
